import {
  Button,
  ButtonColor,
  CardButton,
  Divider,
  EmptyState,
  formatNumericDateTime,
  Hypertext,
  HypertextSize,
  Icon,
  IconName,
  IconSize,
  IconVariant,
  InfoPanel,
  InfoPanelColor,
  PageContainer,
  PageContainerSize,
  Panel,
  Title2,
  Title3,
  Tooltip,
  useDocumentTitle
} from "@quortex/front-common"
import { useSuspenseQuery } from "@tanstack/react-query"
import { isEmpty } from "lodash-es"
import illustrationNoApiKey from "src/App/settings/images/illustration-no-api-key.svg"
import { useOrganizationInfo } from "src/queries/organization"
import { useOrganizationApiKeyList } from "src/queries/organizationApiKeys"

const ApiKeys = () => {
  useDocumentTitle("API keys - General")

  const { data: organization } = useSuspenseQuery(useOrganizationInfo())
  const { data: apiKeys } = useSuspenseQuery({
    ...useOrganizationApiKeyList(),
    select: d => d.results
  })
  const maxOrgApiKeysNb = organization.limits.orgApiKeys || 0
  const apiKeyInfoLabel =
    "Organization API keys give unlimited access to the system on your behalf, you may want to use pool API keys instead for most operations."
  const addApiButton = (
    <Tooltip
      body={`You cannot create more than ${maxOrgApiKeysNb} organization keys`}
      enabled={apiKeys.length >= maxOrgApiKeysNb}
      placement="top"
    >
      <Button
        color={ButtonColor.PRIMARY}
        data-testid="add-api-key-button"
        disabled={apiKeys.length >= maxOrgApiKeysNb}
        to="new"
        type="link"
      >
        <Icon icon={IconName.PLUS} size={IconSize.MEDIUM} type={IconVariant.SOLID} />
        Add an API key
      </Button>
    </Tooltip>
  )
  return (
    <PageContainer size={PageContainerSize.S}>
      <Panel>
        <Title2>Organization API keys</Title2>
        {isEmpty(apiKeys) ? (
          <EmptyState
            className="mt-lg text-center"
            description={apiKeyInfoLabel}
            img={illustrationNoApiKey}
          >
            {addApiButton}
          </EmptyState>
        ) : (
          <>
            <InfoPanel className="mt-lg" color={InfoPanelColor.INFO}>
              {apiKeyInfoLabel}
            </InfoPanel>
            <div className="my-md flex flex-col gap-sm">
              {apiKeys.map((apiKey, index) => (
                <CardButton
                  key={apiKey.uuid}
                  data-testid={`api-key-${index + 1}`}
                  icon={IconName.KEY}
                  to={apiKey.uuid}
                  type="link"
                >
                  <p>
                    {apiKey.name} at {formatNumericDateTime(new Date(apiKey.createdAt))}
                  </p>
                  <p className="text-info">
                    Last used:{" "}
                    {apiKey.lastUsedAt
                      ? formatNumericDateTime(new Date(apiKey.lastUsedAt))
                      : "Never"}
                  </p>
                </CardButton>
              ))}
            </div>
            {addApiButton}
          </>
        )}
        <Divider className="my-md" />
        <div className="px-lg">
          <Title3>Documentation</Title3>
          <InfoPanel className="mt-sm" color={InfoPanelColor.INFO} inline>
            Get started using our API{" "}
            <Hypertext
              className="font-normal"
              external
              href="https://help.quortex.io/en/article/getting-started-with-the-api-1odf9uj/"
              size={HypertextSize.SMALL}
            >
              in this help article
            </Hypertext>
          </InfoPanel>
        </div>
      </Panel>
    </PageContainer>
  )
}

export default ApiKeys
